import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { FiSend, FiCheckCircle, FiEdit2 } from 'react-icons/fi';
import Drawer from '../../components/ui/Drawer';
import Button from '../../components/ui/Button';
import StatusPill from '../../components/ui/StatusPill';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import ErrorBanner from '../../components/ui/ErrorBanner';
import CurrencyDisplay from '../../components/shared/CurrencyDisplay';
import DateDisplay from '../../components/shared/DateDisplay';
import DocumentList from '../../components/ui/DocumentList';
import WorkflowActions from '../../components/ui/WorkflowActions';
import WorkflowTimeline from '../../components/ui/WorkflowTimeline';
import { useAuth } from '../../auth/AuthContext';
import { getProcurement, submitProcurement, updateProcurement } from '../../api/procurement';
import ProcurementFormInner from './ProcurementFormInner';
import { getWorkflowForEntity } from '../../api/workflows';
import { extractErrorMessage } from '../../api/client';
import { WorkflowStatus } from '../../enums';

function Field({ label, children, className = '' }) {
  return (
    <div className={`flex flex-col gap-0.5 ${className}`}>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-ink-muted">
        {label}
      </div>
      <div className="text-sm text-ink">{children ?? <span className="text-ink-faint">—</span>}</div>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <h3 className="border-b border-slate-100 pb-2 text-xs font-semibold uppercase tracking-wider text-ink-muted">
      {children}
    </h3>
  );
}

export default function ProcurementDetailDrawer({ open, onClose, requestId }) {
  const qc = useQueryClient();
  const { user, isAdmin } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editErrors, setEditErrors] = useState({});

  const {
    data: req,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['procurement', requestId],
    queryFn: () => getProcurement(requestId),
    enabled: !!requestId && open,
  });

  const { data: workflow } = useQuery({
    queryKey: ['workflows', 'PROCUREMENT', requestId],
    queryFn: () => getWorkflowForEntity('PROCUREMENT', requestId),
    enabled: !!requestId && open && req && req.workflowStatus !== 'DRAFT',
    retry: false,
  });

  React.useEffect(() => {
    if (req && isEditing) {
      setEditForm({
        title: req.title || '',
        description: req.description || '',
        vendorId: req.vendorId || null,
        projectId: req.projectId || '',
        quantity: req.quantity || 1,
        unitPrice: req.unitPrice || 0,
        amount: req.amount || 0,
      });
      setEditErrors({});
    }
  }, [req, isEditing]);

  const updateMutation = useMutation({
    mutationFn: (payload) => updateProcurement(requestId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['procurement'] });
      qc.invalidateQueries({ queryKey: ['procurement', requestId] });
      toast.success('Procurement request updated successfully');
      setIsEditing(false);
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  function validateEdit() {
    const errs = {};
    if (!editForm.title?.trim()) errs.title = 'Title is required';
    if (!editForm.quantity || Number(editForm.quantity) <= 0) errs.quantity = 'Quantity must be > 0';
    if (editForm.unitPrice === undefined || editForm.unitPrice === null || Number(editForm.unitPrice) < 0) errs.unitPrice = 'Unit price cannot be negative';
    if (!editForm.amount || Number(editForm.amount) <= 0) errs.amount = 'Amount must be > 0';
    setEditErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function handleSaveEdit() {
    if (!validateEdit()) return;
    updateMutation.mutate({
      title: editForm.title.trim(),
      description: editForm.description?.trim() || null,
      amount: Number(editForm.amount),
      quantity: Number(editForm.quantity),
      unitPrice: Number(editForm.unitPrice),
      vendorId: editForm.vendorId || null,
      projectId: editForm.projectId ? Number(editForm.projectId) : null,
    });
  }

  const submitMutation = useMutation({
    mutationFn: () => submitProcurement(requestId),
    onSuccess: () => {
      toast.success('Request submitted for approval');
      qc.invalidateQueries({ queryKey: ['procurement'] });
      qc.invalidateQueries({ queryKey: ['procurement', requestId] });
      qc.invalidateQueries({ queryKey: ['workflows'] });
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  const isOwner =
    !!user?.id && !!req?.requestedBy && Number(user.id) === Number(req.requestedBy);
  const isDraft = req?.workflowStatus === WorkflowStatus.DRAFT;
  const canEditDocs = (isOwner && isDraft) || isAdmin;
  const canSubmit = isDraft && (isOwner || isAdmin);
  const canEdit = isDraft && (isOwner || isAdmin);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={req ? req.title : 'Procurement Request'}
      width="lg"
      footer={
        req ? (
          isEditing ? (
            <div className="flex w-full items-center justify-end gap-2">
              <Button variant="ghost" onClick={() => setIsEditing(false)} disabled={updateMutation.isPending}>
                Cancel
              </Button>
              <Button variant="primary" loading={updateMutation.isPending} onClick={handleSaveEdit}>
                Save Changes
              </Button>
            </div>
          ) : (
            <div className="flex w-full items-center justify-start gap-2">
              {canSubmit && (
                <Button
                  variant="primary"
                  icon={FiSend}
                  loading={submitMutation.isPending}
                  onClick={() => submitMutation.mutate()}
                >
                  Submit for Approval
                </Button>
              )}
              {canEdit && (
                <Button
                  variant="ghost"
                  icon={FiEdit2}
                  onClick={() => setIsEditing(true)}
                >
                  Edit
                </Button>
              )}
            </div>
          )
        ) : null
      }
    >
      {isLoading ? (
        <LoadingSpinner label="Loading request..." />
      ) : error ? (
        <ErrorBanner error={error} />
      ) : !req ? (
        <div className="text-sm text-ink-muted">Request not found.</div>
      ) : isEditing ? (
        <ProcurementFormInner
          form={editForm}
          setForm={setEditForm}
          errors={editErrors}
          isEdit={true}
        />
      ) : (
        <div className="space-y-6">
          {/* Header summary */}
          <div className="rounded-lg border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
                  Request #{req.id}
                </div>
                <div className="mt-0.5 text-base font-semibold text-ink">{req.title}</div>
              </div>
              <StatusPill value={req.workflowStatus} />
            </div>
            {req.description && (
              <p className="mt-3 whitespace-pre-wrap text-sm text-ink-muted">
                {req.description}
              </p>
            )}
          </div>

          {/* Financials */}
          <div>
            <SectionTitle>Details</SectionTitle>
            <div className="mt-3 grid grid-cols-2 gap-4">
              <Field label="Quantity">{req.quantity}</Field>
              <Field label="Unit Price">
                <CurrencyDisplay value={req.unitPrice} />
              </Field>
              <Field label="Total Amount" className="col-span-2">
                <span className="text-base font-semibold">
                  <CurrencyDisplay value={req.amount} />
                </span>
              </Field>
              <Field label="Vendor ID">{req.vendorId ?? '—'}</Field>
              <Field label="Project ID">{req.projectId ?? '—'}</Field>
              <Field label="Requested By">{req.requestedBy ?? '—'}</Field>
              <Field label="Created">
                <DateDisplay value={req.createdAt} withTime />
              </Field>
              {req.workflowStatus === WorkflowStatus.APPROVED && req.approvedBy && (
                <Field label="Approved By" className="col-span-2">
                  <span className="inline-flex items-center gap-1 text-success">
                    <FiCheckCircle /> User #{req.approvedBy}
                  </span>
                </Field>
              )}
            </div>
          </div>

          {/* Workflow */}
          {workflow && (
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <WorkflowTimeline workflow={workflow} />
              {isAdmin && (
                <div className="mt-4 flex justify-end">
                  <WorkflowActions
                    workflow={workflow}
                    invalidateKeys={[
                      ['procurement'],
                      ['procurement', requestId],
                      ['workflows', 'PROCUREMENT', requestId],
                    ]}
                  />
                </div>
              )}
            </div>
          )}

          {/* Documents */}
          <DocumentList
            entityType="PROCUREMENT"
            entityId={req.id}
            department="PROCUREMENT"
            canUpload={canEditDocs}
          />
        </div>
      )}
    </Drawer>
  );
}
